import { pageSizes } from "../config";
/**
 * The only place HTTP happens, under two rules:
 *
 * - `/maker-api/`, the Maker portal's own API, carries the Maker portal session
 *   (`session.ts`) as `Authorization: Bearer`;
 * - each chain's public gateway API (`/api/`, `/api/<chain>/`) NEVER carries a
 *   credential, whatever a caller passes: the browser holds none.
 *
 * A 401 on a `/maker-api/` call that carried the stored session ends it; a 503
 * never does. Amounts stay decimal strings in base units, never floats.
 */

import { endSession, getStoredToken } from "./session";
import { errorReporter } from "./errorReporter";
import type { components, operations } from "./api/makerBackend.gen";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

/** Prefix of the Maker portal's own API. Nothing else ever sees the session. */
export const MAKER_API = "/maker-api";

export class ApiError extends Error {
  readonly status: number | undefined;
  readonly path: string;
  /** `detail` field of a problem-json error body, when present. */
  readonly detail: string | undefined;
  /**
   * Machine-readable `reason` of a problem-json body (`not-an-operator`,
   * `stale-version`, …): the UI branches on this, never on `detail`.
   */
  readonly reason: string | undefined;
  /** The parsed problem body, when there was one. Untyped on purpose. */
  readonly body: Record<string, unknown> | undefined;

  constructor(message: string, path: string, status?: number, detail?: string, reason?: string, body?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.path = path;
    this.status = status;
    this.detail = detail;
    this.reason = reason;
    this.body = body;
  }
}

/** An aborted call (a page left, a network switched): not a failure. */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException ? err.name === "AbortError" : err instanceof Error && err.name === "AbortError";
}

// ---- deployment descriptor (the primary chain's public GET /v1/config) -------

/**
 * Every field is optional and every address nullable (a partly provisioned
 * gateway answers nulls): callers degrade, and no address is ever defaulted to
 * a literal.
 */
export interface ConfigTokenResponse {
  symbol?: string;
  name?: string;
  decimals?: number;
  address?: string | null;
  kind?: string;
  [key: string]: unknown;
}

export interface ConfigResponse {
  chainId?: number;
  network?: string;
  contracts?: { lop?: string | null; settlement?: string | null; [key: string]: unknown };
  tokens?: ConfigTokenResponse[];
  bookTokens?: ConfigTokenResponse[];
  prices?: { pairs?: Record<string, unknown> };
  platformFee?: { state: "known" | "none" | "unknown"; feeBps: string | null; recipient: string | null; [key: string]: unknown };
  explorerBaseUrl?: string;
  /** The WalletConnect project id; absent or null = WalletConnect is not offered. */
  walletConnect?: { projectId?: string | null; [key: string]: unknown } | null;
  [key: string]: unknown;
}

// ---- the Maker portal's API --------------------------------------------------------

type Schema<K extends keyof components["schemas"]> = components["schemas"][K];
type AnswerOf<K extends keyof operations> = operations[K] extends { responses: { 200: { content: { "application/json": infer R } } } } ? R : never;
type BodyOf<K extends keyof operations> = operations[K] extends { requestBody?: { content: { "application/json": infer B } } } ? B : never;
// Preserve named fields when adapting open responses for older API versions.
type Fields<T> = { [K in keyof T as string extends K ? never : number extends K ? never : K]: T[K] };
type Adapt<T, P> = Omit<Fields<T>, keyof P> & P;
type Optional<T, K extends keyof T> = Omit<Fields<T>, K> & Partial<Pick<T, K>>;

export type Address = string;
export type Uint256 = string;
export type Iso = string;
export type PairKey = string;
export type NonceAnswer = Schema<"NonceAnswer">;
export type LoginAnswer = Schema<"LoginAnswer">;
export type HandoffAnswer = Schema<"HandoffAnswer">;
export type ExecutorIdentity = Schema<"ExecutorIdentity">;
export type MakerChain = Schema<"MeAnswer">["chains"][number];
// Optional for older deployed API versions; required once every one sends them.
export type MeAnswer = Optional<Schema<"MeAnswer">, "kind">;
export type SetupStep = Schema<"SetupStep">;
export type ExecutorOwnership = NonNullable<Schema<"ExecutorPage">["ownership"]>;
export type ExecutorPage = Optional<Schema<"ExecutorPage">, "ownership" | "rebalancing">;
export type Bound = Schema<"Bound">;
export type Side = Schema<"LimitsAnswer">["pairBounds"][number]["side"];
export type PairKind = Schema<"QuotesAnswer">["pairs"][number]["kind"];
export type PairBoundRow = Adapt<Optional<Schema<"LimitsAnswer">["pairBounds"][number], "staleness" | "deviation">, { suggestedFrom: "pair" | "desk" | null }>;
export type TokenRef = Pick<Schema<"QuoteTokenRef">, "token" | "symbol" | "decimals">;
export type TokenOrigin = Schema<"QuoteTokenRef">["origin"];
export type PairToken = Optional<Schema<"QuoteTokenRef">, "origin" | "feed">;
export type DailyCapRow = Optional<Schema<"LimitsAnswer">["dailyCaps"][number], "offered">;
export type LimitsAnswer = Adapt<Schema<"LimitsAnswer">, { dailyCaps: DailyCapRow[]; pairBounds: PairBoundRow[] }>;
export type CallKind = Schema<"CallKind">;

export type NativeCallRequest =
  | { kind: "ownership"; params: Record<string, never> }
  | { kind: "filling"; params: { on: boolean } }
  | { kind: "rebalancing"; params: { on: boolean } }
  | { kind: "makerAllowlist"; params: { required: boolean } }
  | {
      kind: "pairBound";
      params: { tokenIn: Address; tokenOut: Address; feed: Address; invert: boolean; maxDeviationBps: number; maxStalenessSec: number };
    }
  | { kind: "dailyCap"; params: { token: Address; cap: Uint256 } }
  | { kind: "treasury"; params: { address: Address } }
  | { kind: "allowance"; params: { token: Address; amount: Uint256 } }
  | { kind: "lossBudget"; params: { token: Address; budget: Uint256 } }
  | { kind: "router"; params: { router: Address; allowed: boolean } };

export type ZeroexView = Schema<"ZeroexView">;
export type ZeroexState = Schema<"ZeroexState">;
export type ZeroexAction = Schema<"ZeroexAction">;
export type ZeroexKind = Schema<"ZeroexKind">;
export type ZeroexToken = ZeroexView["tokens"][number];
export type ZeroexPair = ZeroexView["pairs"][number];
export type ZeroexBound = NonNullable<ZeroexPair["directions"][number]["bound"]>;
export type ZeroexTreasury = NonNullable<ZeroexView["treasury"]>;
export type ZeroexDeploy = NonNullable<ZeroexView["deploy"]>;
export type ZeroexCall = Schema<"ZeroexCall">;
export type ZeroexCallBody = BodyOf<"prepareZeroexCall">;
export type ZeroexDeployPrepared = Schema<"ZeroexDeployPrepared">;
export type ZeroexReported = Schema<"ZeroexReported">["call"];
export type CallRequest = NativeCallRequest;
export type PreparedCall = Omit<Schema<"PreparedCall">["call"], "kind"> & { kind: string };
export type ReportedCall = Schema<"ReportedCall">["call"];
export type TreasuryAnswer = Adapt<Schema<"TreasuryAnswer">, { tokens: Optional<Schema<"TreasuryAnswer">["tokens"][number], "offered">[] }>;
export type BookFeed = Schema<"BookFeed">;
export type PairSetting = Schema<"PairSetting">;
export type PriceSourceSetting = NonNullable<PairSetting["priceSource"]>;
export type DefaultPairSetting = Schema<"DefaultPairSetting">;
export type Reference = Schema<"Reference">;
export type QuotingReason = Schema<"QuotingReason">;
type WirePairStatus = Schema<"BookStatus">["pairs"][string];
type WirePrice = NonNullable<WirePairStatus["price"]>;
export type SideStatus = Adapt<WirePairStatus["bid"], { pays: Fields<WirePairStatus["bid"]["pays"]> }>;
// The view keeps the existing price discriminant used by feed and pair controls.
export type DeskPrice = Adapt<WirePrice, { source: Exclude<WirePrice["source"], "book">; deskBidBps: number; deskAskBps: number }>;
export type BookPrice = Adapt<WirePrice, { source: "book"; deskBidBps: null; deskAskBps: null; feed: NonNullable<WirePrice["feed"]>; pool?: null }>;
export type PairStatus = Adapt<WirePairStatus, { kind?: PairKind | null; bid: SideStatus; ask: SideStatus; price: DeskPrice | BookPrice | null }>;
export type SourcePreview = NonNullable<WirePairStatus["pricingSource"]>;
export type OrderSettings = Fields<Schema<"BookStatus">["order"]>;
export type BookStatus = Adapt<Schema<"BookStatus">, { pairs: Record<PairKey, PairStatus> }>;
export type PushState = Schema<"QuotesAnswer">["push"]["state"];
export type QuotesPair = Adapt<Schema<"QuotesAnswer">["pairs"][number], { base: PairToken; quote: PairToken }>;
export type AddablePair = Adapt<Schema<"QuotesAnswer">["ownPairs"]["addable"][number], { base: PairToken; quote: PairToken }>;
export type OwnPairs = Adapt<Schema<"QuotesAnswer">["ownPairs"], { addable: AddablePair[] }>;
// `priceApi` absent (an older Maker portal API) reads as no price API configured on the network.
export type QuotesAnswer = Adapt<Optional<Schema<"QuotesAnswer">, "ranges" | "priceApi">, { pairs: QuotesPair[]; ownPairs: OwnPairs; backend: BookStatus | null }>;
export type FeedCheckAnswer = Schema<"FeedCheckAnswer">;
export type FeedRefusal = NonNullable<FeedCheckAnswer["refusal"]>;
export type DirectoryFeed = Schema<"DirectoryFeed">;
export type FeedDirectory = Schema<"FeedDirectory">;
export type RebalancingPair = Optional<Schema<"RebalancingAnswer">["pairs"][number], "automatic">;
export type RebalancingAnswer = Adapt<Optional<Schema<"RebalancingAnswer">, "automatic">, { pairs: RebalancingPair[] }>;
export type HistoryOrder = Schema<"HistoryOrder">;
export type OrderStatus = HistoryOrder["status"];
export type HistoryFill = HistoryOrder["fills"][number];
export type OrdersAnswer = Schema<"OrdersAnswer">;
export type RuleKind = Schema<"RuleKind">;
export type NotificationEvent = Optional<Schema<"NotificationEvent">, "chats">;
export type FillTally = Schema<"MonitoringAnswer">["fills"]["day"];
export type TransactionRow = Schema<"TransactionsAnswer">["transactions"][number] & Partial<Pick<Schema<"MonitoringAnswer">["transactions"][number], "executor">>;
export type TransactionType = "all" | TransactionRow["type"];
export type TransactionsPage = Adapt<Schema<"TransactionsAnswer">, { transactions: TransactionRow[] }>;
export type EventsPage = Adapt<Schema<"NotificationEventsPage">, { events: NotificationEvent[] }>;
type WireMonitoring = Schema<"MonitoringAnswer">;
export type MonitoringAnswer = Adapt<WireMonitoring, {
  treasury: Adapt<WireMonitoring["treasury"], { tokens: Optional<WireMonitoring["treasury"]["tokens"][number], "offered">[] }>;
  approvals: Optional<WireMonitoring["approvals"][number], "offered">[];
  transactions: Optional<WireMonitoring["transactions"][number], "executor">[];
  events: NotificationEvent[];
}>;
export type Rule = Optional<Schema<"Rule">, "set">;
export type ChatType = NonNullable<Schema<"TelegramChat">["type"]>;
export type StopReason = NonNullable<Schema<"TelegramChat">["stopped"]>["reason"];
export type TelegramChat = Adapt<Schema<"TelegramChat">, { stopped: Adapt<NonNullable<Schema<"TelegramChat">["stopped"]>, { reason: StopReason | (string & {}) }> | null }>;
export type PendingLink = Schema<"PendingLink">;
export type TelegramLink = Schema<"TelegramLink">;
export type ChatTest = Schema<"ChatTest">;
export type ChatRemoved = Schema<"ChatRemoved">;
export type NotificationsAnswer = Adapt<Schema<"NotificationsAnswer">, {
  telegram: Adapt<Omit<Schema<"NotificationsAnswer">["telegram"], "chatId">, { chats: TelegramChat[] }>;
  events: NotificationEvent[];
  rules: Rule[];
}>;
/** Historical venue rows retained by the transaction table; no desk settings API. */
export interface VenueExecutor {
  address: string; role: "market-maker" | "solver"; current: boolean; editable: boolean; fillable: boolean;
  lop: string | null; settlement: string | null; owner: string | null; operator: string | null;
  rebalancer: string | null; treasury: string | null; admitted: boolean | null; makerAllowlistRequired: boolean | null; registeredAt: string | null;
}
export type MakerRisk = Schema<"MakerRiskAnswer">;
export type MakerRiskInput = BodyOf<"saveMakerRisk">;
export type MakerRebalancing = Schema<"MakerRebalancingSettingsAnswer">;
export type MakerRebalancingInput = BodyOf<"saveMakerRebalancingSettings">;
export type MakerPositions = AnswerOf<"getMakerPositions">;
export type MakerOperators = AnswerOf<"getMakerOperators">["operators"];
export type Range = MakerRisk["ranges"]["maxLossBps"];
export type Applied = MakerRisk["applied"];

// ---- the one fetch path ------------------------------------------------------

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /**
   * `/maker-api/` calls only (a gateway call never carries a credential):
   * `undefined` (default) sends the stored session; `null` sends none (the
   * sign-in calls).
   */
  bearer?: null;
  /** Skip error reporting (polls, the boot check). */
  silent?: boolean;
  /**
   * HTTP statuses that are expected, handled states. They still throw
   * ApiError so the caller can branch, but are NOT reported.
   */
  expectedStatuses?: readonly number[];
  signal?: AbortSignal | undefined;
}

/** True for a path of the Maker portal's own API: the only one that may carry the session. */
export function isMakerApiPath(path: string): boolean {
  return path === MAKER_API || path.startsWith(`${MAKER_API}/`) || path.startsWith(`${MAKER_API}?`);
}

/** The answer with its HTTP status, for the calls whose 2xx statuses differ (the report's 200 and 202). */
export interface WithStatus<T> {
  status: number;
  body: T;
}

/** How far the Maker portal's API clock is ahead of this browser's, from its last answer's `Date` header. */
let serverSkewMs = 0;

/** Now on the API's clock: what a countdown to a time the API set is measured against. */
export function serverNow(): number {
  return Date.now() + serverSkewMs;
}

function recordServerClock(res: Response): void {
  const date = typeof res.headers?.get === "function" ? res.headers.get("date") : null;
  const at = date === null ? Number.NaN : Date.parse(date);
  if (Number.isFinite(at)) serverSkewMs = at - Date.now();
}

/** Tests only. */
export function resetServerClock(): void {
  serverSkewMs = 0;
}

async function send<T>(path: string, options: RequestOptions): Promise<WithStatus<T>> {
  const url = `${BASE_URL}${path}`;
  const init: RequestInit = { method: options.method ?? "GET" };
  const headers: Record<string, string> = {};
  // The session goes to the Maker portal's API and nowhere else: a gateway
  // path gets no Authorization header whatever the caller asked for.
  const makerApi = isMakerApiPath(path);
  const token = makerApi && options.bearer === undefined ? getStoredToken() : null;
  if (token !== null && token !== "") headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  if (Object.keys(headers).length > 0) init.headers = headers;
  if (options.signal !== undefined) init.signal = options.signal;

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    if (isAbortError(err)) throw err;
    const apiError = new ApiError(`Network error calling ${path}: ${err instanceof Error ? err.message : String(err)}`, path);
    if (!options.silent) errorReporter.capture(apiError.message, { level: "error", meta: { source: "api", path } });
    throw apiError;
  }

  if (makerApi) recordServerClock(res);

  if (!res.ok) {
    let bodyText = "";
    try {
      // The whole body is parsed (a test's per-chat answers can be long); only the message is cut.
      bodyText = await res.text();
    } catch {
      // response body unreadable: status alone is enough
    }
    let problemDetail: string | undefined;
    let problemReason: string | undefined;
    let problemBody: Record<string, unknown> | undefined;
    try {
      const parsed: unknown = JSON.parse(bodyText);
      if (parsed !== null && typeof parsed === "object") {
        const problem = parsed as { detail?: unknown; reason?: unknown };
        if (typeof problem.detail === "string") problemDetail = problem.detail;
        if (typeof problem.reason === "string") problemReason = problem.reason;
        problemBody = parsed as Record<string, unknown>;
      }
    } catch {
      // not JSON: fine
    }
    const apiError = new ApiError(`API ${path} failed with ${res.status}${bodyText ? `: ${bodyText.slice(0, 300)}` : ""}`, path, res.status, problemDetail, problemReason, problemBody);
    const expected = options.expectedStatuses?.includes(res.status) ?? false;
    if (!options.silent && !expected) {
      errorReporter.capture(apiError.message, { level: "error", meta: { source: "api", path, status: res.status } });
    }
    // The stored session was refused: it is over. Never on a 503: a database
    // or chain that cannot answer says nothing about the session. Only while
    // the refused token is still the stored one: another tab may have stored
    // a newer session meanwhile (a sign-in, the admin console's handoff), and
    // a late 401 about the old one must not end it.
    if (token !== null && res.status === 401 && getStoredToken() === token) endSession("ended");
    throw apiError;
  }

  if (res.status === 204) return { status: res.status, body: undefined as T };
  return { status: res.status, body: (await res.json()) as T };
}

/**
 * The one fetch path. Exported for tests of the credential rules; pages call
 * the typed `api` methods below instead.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await send<T>(path, options)).body;
}

/** Handled outcomes of every Maker portal call: refusals the pages word themselves. */
const HANDLED: readonly number[] = [400, 401, 403, 404, 409, 422, 429, 502, 503];

/** A field an older Maker portal API leaves out reads as absent. */
type Older<T> = { [K in keyof T]?: T[K] | undefined };

/**
 * Own pairs' fields read tolerantly: an older Maker portal API answers without
 * them, and then a network takes no own pair. Drop once every API sends them.
 */
function quotesAnswer(raw: Older<QuotesAnswer>): QuotesAnswer {
  const push: Older<QuotesAnswer["push"]> = raw.push ?? {};
  return {
    ...(raw.quoteSources ? { quoteSources: raw.quoteSources } : {}),
    ...(raw.priceApi ? { priceApi: raw.priceApi } : {}),
    book: raw.book ?? null,
    defaults: {},
    ranges: raw.ranges ?? null,
    pairs: (raw.pairs ?? []).map((p: Older<QuotesPair> & Pick<QuotesPair, "pair" | "base" | "quote">) => ({
      ...p,
      kind: p.kind ?? "desk",
      feed: p.feed ?? null,
      offered: p.offered ?? true,
    })),
    ownPairs: raw.ownPairs ?? { supported: false, state: null, referenceBandBps: null, maxFeedAgeSec: null, lifeSec: null, maxPerBook: 0, addable: [], why: "backend-not-ready" },
    push: { state: push.state ?? "off", at: push.at ?? null, refusal: push.refusal ?? null },
    backend: raw.backend ?? null,
  };
}

function limitsAnswer(raw: Older<LimitsAnswer>): LimitsAnswer {
  return {
    executor: raw.executor ?? null,
    owner: raw.owner ?? null,
    filling: raw.filling ?? { operator: null, filler: null, on: false },
    makerAllowlistRequired: raw.makerAllowlistRequired ?? null,
    pairBounds: (raw.pairBounds ?? []).map((r: Older<PairBoundRow> & Omit<PairBoundRow, "kind" | "suggestedFrom" | "matchesPair">) => ({
      ...r,
      kind: r.kind ?? "desk",
      suggestedFrom: r.suggestedFrom === "pair" ? "pair" : null,
      matchesPair: r.matchesPair ?? null,
    })),
    dailyCaps: raw.dailyCaps ?? [],
    ...(raw.swapLimits === undefined ? {} : { swapLimits: raw.swapLimits }),
  };
}

/** An answer without the chat grid reads as no chat linked. */
function notificationsAnswer(raw: Older<NotificationsAnswer>): NotificationsAnswer {
  const telegram: Older<NotificationsAnswer["telegram"]> = raw.telegram ?? {};
  return {
    telegram: { delivery: telegram.delivery ?? "unconfigured", bot: telegram.bot ?? null, chats: telegram.chats ?? [], pendingLink: telegram.pendingLink ?? null },
    rules: raw.rules ?? [],
    events: raw.events ?? [],
  };
}

const chainPath = (chainId: number, rest: string) => `${MAKER_API}/chains/${chainId}/${rest}`;
type Opts = { signal?: AbortSignal | undefined; silent?: boolean | undefined };

export const api = {
  // ---- the public gateway: no credential, ever ---------------------------------

  /** The primary gateway's descriptor, fetched once at boot; 404 (an older gateway without the route) is handled. */
  config(): Promise<ConfigResponse> {
    return request<ConfigResponse>("/api/v1/config", { expectedStatuses: [404] });
  },

  /** A network's own descriptor, for the chain id a WalletConnect session is proposed for; 404 is handled. */
  chainConfig(apiBase: string, opts: Opts = {}): Promise<ConfigResponse> {
    return request<ConfigResponse>(`${apiBase}/v1/config`, { expectedStatuses: [404], signal: opts.signal });
  },

  /** The integration document: the gateway's own OpenAPI 3.1, cut to what a partner calls. */
  openApi(apiBase: string): Promise<import("./openapi").OaDocument> {
    return request<import("./openapi").OaDocument>(`${apiBase}/v1/integration/openapi.json`, { expectedStatuses: [404, 503] });
  },

  /**
   * One request for the reference's "Try it", reported WHATEVER it answers,
   * and sent with no credential: this page holds none. Only a transport
   * failure rejects.
   */
  async tryIt(url: string, init: { method: string; body?: string | undefined }): Promise<{ status: number; ms: number; bytes: number; body: string }> {
    const started = performance.now();
    const res = await fetch(url, {
      method: init.method,
      headers: {
        Accept: "application/json",
        ...(init.body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(init.body !== undefined ? { body: init.body } : {}),
    });
    const text = await res.text();
    let pretty = text;
    try {
      pretty = JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      // not JSON: shown as it came
    }
    return { status: res.status, ms: Math.round(performance.now() - started), bytes: new Blob([text]).size, body: pretty };
  },

  // ---- sign-in: no session header -----------------------------------------------

  nonce(): Promise<NonceAnswer> {
    return request<NonceAnswer>(`${MAKER_API}/auth/nonce`, { method: "POST", bearer: null, expectedStatuses: [429, 503] });
  },

  /** 401 `invalid-message` / `invalid-signature`, 403 `not-an-operator`, 429, 503. */
  login(body: BodyOf<"signIn">): Promise<LoginAnswer> {
    return request<LoginAnswer>(`${MAKER_API}/auth/login`, { method: "POST", body, bearer: null, expectedStatuses: [400, 401, 403, 429, 503] });
  },

  /**
   * Spends the admin console's one-time code. Never reported from here: the body holds the code, so
   * the caller reports by status alone. 401 `invalid-handoff` (malformed, unknown, used or expired),
   * 400, 429 `rate-limited`, 503 `maker-portal-unconfigured` or `chain-unavailable`.
   */
  handoff(code: string): Promise<HandoffAnswer> {
    return request<HandoffAnswer>(`${MAKER_API}/auth/handoff`, { method: "POST", body: { code } satisfies BodyOf<"exchangeHandoff">, bearer: null, silent: true });
  },

  /** Who the session is; 401 = it is over. */
  me(opts: Opts & { silent?: boolean } = {}): Promise<MeAnswer> {
    return request<MeAnswer>(`${MAKER_API}/auth/me`, { expectedStatuses: [401, 503], signal: opts.signal, ...(opts.silent === true ? { silent: true } : {}) });
  },

  // ---- executor, limits and owner calls ------------------------------------------

  executor(chainId: number, opts: Opts & { silent?: boolean } = {}): Promise<ExecutorPage> {
    return request<ExecutorPage>(chainPath(chainId, "executor"), { expectedStatuses: HANDLED, signal: opts.signal, ...(opts.silent === true ? { silent: true } : {}) });
  },

  async limits(chainId: number, opts: Opts = {}): Promise<LimitsAnswer> {
    return limitsAnswer(await request<Older<LimitsAnswer>>(chainPath(chainId, "limits"), { expectedStatuses: HANDLED, signal: opts.signal }));
  },

  async prepareCall(chainId: number, call: NativeCallRequest): Promise<PreparedCall> {
    const res = await request<Schema<"PreparedCall">>(chainPath(chainId, "calls/prepare"), { method: "POST", body: call satisfies BodyOf<"prepareOwnerCall">, expectedStatuses: HANDLED });
    return res.call;
  },

  /** 200 confirmed, 202 pending (report again); 409 `call-mismatch`, 422 `call-failed`, 404 `call-not-found`. */
  async reportCall(chainId: number, body: BodyOf<"reportOwnerCall">): Promise<ReportedCall> {
    const res = await send<Schema<"ReportedCall">>(chainPath(chainId, "calls/report"), { method: "POST", body, expectedStatuses: HANDLED });
    return res.body.call;
  },

  // ---- the 0x treasury ------------------------------------------------------------

  /** One network's 0x treasury; a network that cannot be read answers its state `unreadable`, not 503. */
  zeroex(chainId: number, opts: Opts = {}): Promise<ZeroexView> {
    return request<ZeroexView>(chainPath(chainId, "zeroex"), { expectedStatuses: HANDLED, signal: opts.signal });
  },

  /**
   * CreateX's deployment for the generation's deploying wallet, else `deployer`, else the session's:
   * the page checks it before a wallet sees it. A session without a wallet must name `deployer`.
   */
  prepareZeroexDeploy(chainId: number, deployer?: string): Promise<ZeroexDeployPrepared> {
    const body: BodyOf<"prepareZeroexDeploy"> = deployer === undefined ? {} : { deployer };
    return request<ZeroexDeployPrepared>(chainPath(chainId, "zeroex/deploy/prepare"), { method: "POST", body, expectedStatuses: HANDLED });
  },

  /** The owner calls of one review, each its own transaction (a pair's price limits: both directions). */
  async prepareZeroexCalls(chainId: number, body: ZeroexCallBody): Promise<ZeroexCall[]> {
    return (await request<Schema<"ZeroexCallsPrepared">>(chainPath(chainId, "zeroex/calls/prepare"), { method: "POST", body, expectedStatuses: HANDLED })).calls;
  },

  /** 200 confirmed (a deployment: recorded), 202 pending (report again); 409 `call-mismatch`, 422 `call-failed`, 404 `call-not-found`. */
  async reportZeroex(chainId: number, body: BodyOf<"reportZeroexCall">): Promise<ZeroexReported> {
    return (await send<Schema<"ZeroexReported">>(chainPath(chainId, "zeroex/calls/report"), { method: "POST", body, expectedStatuses: HANDLED })).body.call;
  },

  risk(chainId: number, opts: Opts = {}): Promise<MakerRisk> {
    return request(chainPath(chainId, "risk"), { expectedStatuses: HANDLED, signal: opts.signal });
  },
  saveRisk(chainId: number, body: MakerRiskInput): Promise<MakerRisk> {
    return request(chainPath(chainId, "risk"), { method: "PUT", body, expectedStatuses: HANDLED });
  },
  positions(chainId: number, opts: Opts = {}): Promise<MakerPositions> {
    return request(chainPath(chainId, "positions"), { expectedStatuses: HANDLED, signal: opts.signal });
  },
  resetPositions(chainId: number): Promise<MakerPositions> {
    return request(chainPath(chainId, "positions/reset"), { method: "POST", body: {}, expectedStatuses: HANDLED });
  },
  rebalancingSettings(chainId: number, opts: Opts = {}): Promise<MakerRebalancing> {
    return request(chainPath(chainId, "rebalancing/settings"), { expectedStatuses: HANDLED, signal: opts.signal });
  },
  saveRebalancing(chainId: number, body: MakerRebalancingInput): Promise<MakerRebalancing> {
    return request(chainPath(chainId, "rebalancing/settings"), { method: "PUT", body, expectedStatuses: HANDLED });
  },
  async operators(opts: Opts = {}): Promise<MakerOperators> {
    return (await request<AnswerOf<"getMakerOperators">>(`${MAKER_API}/operators`, { expectedStatuses: HANDLED, signal: opts.signal })).operators;
  },
  addOperator(address: string): Promise<AnswerOf<"addMakerOperator">> {
    return request(`${MAKER_API}/operators`, { method: "POST", body: { address }, expectedStatuses: HANDLED });
  },
  removeOperator(address: string): Promise<AnswerOf<"removeMakerOperator">> {
    return request(`${MAKER_API}/operators/${encodeURIComponent(address)}`, { method: "DELETE", expectedStatuses: HANDLED });
  },

  // ---- treasury ------------------------------------------------------------------

  treasury(chainId: number, opts: Opts = {}): Promise<TreasuryAnswer> {
    return request<TreasuryAnswer>(chainPath(chainId, "treasury"), { expectedStatuses: HANDLED, signal: opts.signal });
  },

  /** Before deployment only (409 `executor-deployed` after it). */
  setPlannedTreasury(chainId: number, address: Address): Promise<TreasuryAnswer> {
    return request<TreasuryAnswer>(chainPath(chainId, "treasury"), { method: "PUT", body: { address } satisfies BodyOf<"setPlannedTreasury">, expectedStatuses: HANDLED });
  },

  // ---- quotes --------------------------------------------------------------------

  async quotes(chainId: number, opts: Opts = {}): Promise<QuotesAnswer> {
    return quotesAnswer(await request<Older<QuotesAnswer>>(chainPath(chainId, "quotes"), { expectedStatuses: HANDLED, signal: opts.signal, ...(opts.silent === undefined ? {} : { silent: opts.silent }) }));
  },

  /**
   * The whole book (a pair is removed by leaving it out); 409 `stale-version` when another operator
   * saved first. A new own pair or a changed feed is checked by the backend first: a refusal is a 400
   * with its reason, no answer a 503 `gateway-unavailable`, and nothing is saved.
   */
  async setQuotes(chainId: number, body: Adapt<BodyOf<"setQuotes">, { pairs: Record<PairKey, PairSetting> }>): Promise<QuotesAnswer> {
    return quotesAnswer(await request<Older<QuotesAnswer>>(chainPath(chainId, "quotes"), { method: "PUT", body, expectedStatuses: HANDLED }));
  },

  /** The backend's check of a feed for an own pair, for the Add pair dialog's preview; 429 `rate-limited`, 503 `gateway-unavailable`. */
  feedCheck(chainId: number, body: Required<BodyOf<"checkFeed">>, opts: Opts = {}): Promise<FeedCheckAnswer> {
    return request<FeedCheckAnswer>(chainPath(chainId, "quotes/feed-check"), { method: "POST", body, expectedStatuses: HANDLED, signal: opts.signal });
  },

  /**
   * Chainlink's directory of the network's feeds; a cold read may take up to 10 s. 503
   * `feeds-unavailable`, 404 `feeds-not-listed` (or an older API's bare 404). Read loosely
   * (`readDirectory`): the backend checks the feed that is saved.
   */
  feeds(chainId: number, opts: Opts = {}): Promise<FeedDirectory> {
    return request<FeedDirectory>(chainPath(chainId, "feeds"), { expectedStatuses: HANDLED, signal: opts.signal });
  },

  // ---- rebalancing ---------------------------------------------------------------

  rebalancing(chainId: number, opts: Opts & { silent?: boolean } = {}): Promise<RebalancingAnswer> {
    return request<RebalancingAnswer>(chainPath(chainId, "rebalancing"), { expectedStatuses: HANDLED, signal: opts.signal, ...(opts.silent === true ? { silent: true } : {}) });
  },

  /** A target share and its tolerance per pair (the API's default, 5, without `tolerancePct`); null clears the pair's target. */
  setTargets(chainId: number, targets: BodyOf<"setRebalancingTargets">["targets"], expectedVersion?: number): Promise<RebalancingAnswer> {
    return request<RebalancingAnswer>(chainPath(chainId, "rebalancing/targets"), { method: "PUT", body: { targets, ...(expectedVersion === undefined ? {} : { expectedVersion }) } satisfies BodyOf<"setRebalancingTargets">, expectedStatuses: HANDLED });
  },

  // ---- orders and monitoring ----------------------------------------------

  /** `before` is the previous page's `nextBefore`, passed on unchanged. */
  orders(chainId: number, params: { limit?: number; before?: string | null } = {}, opts: Opts = {}): Promise<OrdersAnswer> {
    const qs = new URLSearchParams({ limit: String(params.limit ?? pageSizes.orders) });
    if (params.before !== undefined && params.before !== null) qs.set("before", params.before);
    return request<OrdersAnswer>(`${chainPath(chainId, "orders")}?${qs.toString()}`, { expectedStatuses: HANDLED, signal: opts.signal });
  },

  monitoring(chainId: number, opts: Opts = {}): Promise<MonitoringAnswer> {
    return request<MonitoringAnswer>(chainPath(chainId, "monitoring"), { expectedStatuses: HANDLED, signal: opts.signal });
  },

  /** A page of the network's transactions; `before` is the previous page's `nextBefore`. 400 `invalid-cursor`, 503 `chain-unavailable`. */
  transactions(chainId: number, params: { limit: number; type: TransactionType; before: string | null }, opts: Opts & { silent?: boolean } = {}): Promise<TransactionsPage> {
    const qs = new URLSearchParams({ limit: String(params.limit), type: params.type });
    if (params.before !== null) qs.set("before", params.before);
    return request<TransactionsPage>(`${chainPath(chainId, "transactions")}?${qs.toString()}`, { expectedStatuses: HANDLED, signal: opts.signal, ...(opts.silent === true ? { silent: true } : {}) });
  },

  // ---- notifications: every network -----------------------------------------------

  async notifications(opts: Opts & { silent?: boolean } = {}): Promise<NotificationsAnswer> {
    return notificationsAnswer(
      await request<Older<NotificationsAnswer>>(`${MAKER_API}/notifications`, { expectedStatuses: HANDLED, signal: opts.signal, ...(opts.silent === true ? { silent: true } : {}) }),
    );
  },

  /** A page of the events of every network, newest first; `before` is the previous page's `nextBefore`. 400 `invalid-cursor`. */
  notificationEvents(params: { limit: number; before: string | null }, opts: Opts & { silent?: boolean } = {}): Promise<EventsPage> {
    const qs = new URLSearchParams({ limit: String(params.limit) });
    if (params.before !== null) qs.set("before", params.before);
    return request<EventsPage>(`${MAKER_API}/notifications/events?${qs.toString()}`, { expectedStatuses: HANDLED, signal: opts.signal, ...(opts.silent === true ? { silent: true } : {}) });
  },

  /** Rules not named keep what they are. 400 `invalid-rule`. */
  async setNotifications(body: BodyOf<"setNotifications">): Promise<NotificationsAnswer> {
    return notificationsAnswer(await request<Older<NotificationsAnswer>>(`${MAKER_API}/notifications`, { method: "PUT", body, expectedStatuses: HANDLED }));
  },

  /**
   * To every chat that receives, each answering for itself. 409 `no-chat`, 503
   * `delivery-unconfigured`, 502 `delivery-failed` (no chat took it; the problem carries `chats`).
   */
  testNotification(): Promise<Schema<"TestSent">> {
    return request<Schema<"TestSent">>(`${MAKER_API}/notifications/test`, { method: "POST", expectedStatuses: HANDLED });
  },

  /** A new link to AileFX's bot; the one this session asked for before stops working. 503 `telegram-unconfigured` or `telegram-unavailable`, 409 `too-many-chats`. */
  telegramLink(): Promise<TelegramLink> {
    return request<TelegramLink>(`${MAKER_API}/notifications/telegram/links`, { method: "POST", expectedStatuses: HANDLED });
  },

  /** Cancels this session's open link (204, also when there is none). */
  cancelTelegramLink(): Promise<void> {
    return request<void>(`${MAKER_API}/notifications/telegram/links`, { method: "DELETE", expectedStatuses: HANDLED });
  },

  /** 404 `chat-not-found` when it is gone already. */
  removeTelegramChat(id: string): Promise<ChatRemoved> {
    return request<ChatRemoved>(`${MAKER_API}/notifications/telegram/chats/${encodeURIComponent(id)}`, { method: "DELETE", expectedStatuses: HANDLED });
  },
};
